import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as d3 from "d3";
import { addDays, format } from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAssetPowerSocTrendsQuery } from "../../services/query/project-asset-details.query";
import useAppConfigStore from "../../store/useAppConfigStore";
const CHART_MARGIN = { bottom: 60, left: 80, right: 80, top: 50 };
/** Minimum pixels per day — controls how wide the SVG grows as pages are loaded. */
const PIXELS_PER_DAY = 120;
/** Distance from the right edge (px) that triggers the next page pre-fetch. */
const SCROLL_TRIGGER_PX = 300;
const SOC_MAX = 100;
const DEFAULT_MAX_POWER = 100;
const AXIS_TICK_COUNT = 5;
const LINE_STROKE_WIDTH = 2;
const DATA_POINT_RADIUS = 3;
const AXIS_FONT_SIZE = "12px";
const LABEL_FONT_SIZE = "14px";
const LEGEND_FONT_SIZE = "11px";
const AXIS_TEXT_COLOR = "grey";
const LABEL_TEXT_COLOR = "black";
const LABEL_FONT_WEIGHT = "500";
const LABEL_FONT_FAMILY = "Sans-serif";
const COLOR_SOC = "#1AC69E";
const COLOR_POWER = "#FB5A00";
const GRID_LINE_COLOR = "#e0e0e0";
const GRID_STROKE_WIDTH = 0.5;
const GRID_DASH_ARRAY = "2,2";
const LEGEND_X_OFFSET = 200;
const LEGEND_Y_OFFSET = -30;
const LEGEND_ITEM_SPACING = 100;
const LEGEND_TEXT_X = 18;
const LEGEND_RECT_HEIGHT = 2;
const LEGEND_RECT_WIDTH = 12;
const X_LABEL_Y_OFFSET = 20;
const Y_LABEL_LEFT_OFFSET = 20;
const Y_LABEL_RIGHT_OFFSET = 30;
const POWER_TICK_COUNT = 5;
const LEGEND_DATA = [
    { color: COLOR_SOC, label: "SOC (%)" },
    { color: COLOR_POWER, label: "Power (kW)" }
];
/** Returns yyyy-MM-dd for the given Date. */
const toYMD = (d) => format(d, "yyyy-MM-dd");
const AssetDetailsLineChart = ({ assetId, date, height, projectId, toDate, width }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const isFetchingMore = useRef(false);
    const margin = CHART_MARGIN;
    const formattedTodayDate = format(new Date(), "yyyy-MM-dd");
    const baseToDate = toDate ? toDate.split("T")[0] : formattedTodayDate;
    // When no explicit start date is given, default to 30 days ago (full window).
    const defaultFromDate = toYMD(addDays(new Date(baseToDate + "T00:00:00Z"), -29));
    const baseFromDate = date ?? defaultFromDate;
    // Fixed full-range filters — the entire window is always sent in the request body.
    // Cursor pagination (driven by user scroll) handles splitting the result into pages.
    const pageFromFull = `${baseFromDate}T00:00:00.000Z`;
    const pageToFull = `${baseToDate}T23:59:59.999Z`;
    // Adaptive sampling frequency based on window size — keeps data volume manageable.
    //   ≤11 days  →  5-min  (~288–1,584 points)
    //   ≤14 days  →  30-min (~528–672 points)
    //   30 days   →  60-min (~720 points)
    const windowDays = Math.max(1, Math.round((new Date(baseToDate + "T00:00:00Z").getTime() - new Date(baseFromDate + "T00:00:00Z").getTime()) /
        (1000 * 60 * 60 * 24)) + 1);
    const socFrequencyMinutes = windowDays <= 1 ? 5 : windowDays <= 14 ? 30 : 60;
    // --- Pagination state ---
    const [hasMore, setHasMore] = useState(false);
    const [allTrendsData, setAllTrendsData] = useState([]);
    /** Cursor currently being queried — null = initial fetch of the full range. */
    const [currentCursor, setCurrentCursor] = useState(null);
    /** Next cursor returned by the last response, available once the user scrolls. */
    const [pendingNextCursor, setPendingNextCursor] = useState(null);
    // Reset when the base date range changes
    useEffect(() => {
        setHasMore(false);
        setAllTrendsData([]);
        setCurrentCursor(null);
        setPendingNextCursor(null);
    }, [baseFromDate, baseToDate]);
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { data: pageData, isFetching } = useAssetPowerSocTrendsQuery(projectId, baseToDate, // startDate URL param = end of window (today); range is in the body filters
    assetId, socFrequencyMinutes, undefined, pageFromFull, pageToFull, currentCursor ?? undefined);
    // Accumulate data; when the response has a nextCursor, store it so the user
    // can scroll to load the next cursor page — do NOT auto-advance the cursor.
    useEffect(() => {
        if (isFetching || !pageData?.assetTrendsDetails?.length)
            return;
        const assetEntry = pageData.assetTrendsDetails.find((d) => d.assetId === assetId) ?? pageData.assetTrendsDetails[0];
        const newTrends = assetEntry?.trends ?? [];
        setAllTrendsData((prev) => {
            const existingTs = new Set(prev.map((d) => d.timestamp));
            const fresh = newTrends
                .map((d) => {
                if (d.timestamp && !d.timestamp.includes("T")) {
                    // Time-only timestamps (e.g. "12:00:00") are prefixed with baseToDate
                    // because the API call uses baseToDate as the startDate URL param —
                    // the backend anchors time-only values to that day, not the window start.
                    return { ...d, timestamp: `${baseToDate}T${d.timestamp}` };
                }
                return d;
            })
                .filter((d) => !existingTs.has(d.timestamp));
            return [...prev, ...fresh];
        });
        const nextCursor = pageData.responseMetadata?.nextCursor;
        if (nextCursor) {
            setPendingNextCursor(nextCursor);
            setHasMore(true);
        }
        else {
            setPendingNextCursor(null);
            setHasMore(false);
        }
        isFetchingMore.current = false;
    }, [pageData, isFetching, assetId, baseToDate]);
    // Scroll handler: near the right edge → advance to the next cursor page
    const handleScroll = useCallback(() => {
        const el = containerRef.current;
        if (!el || isFetching || isFetchingMore.current || !hasMore || !pendingNextCursor)
            return;
        const distFromRight = el.scrollWidth - el.scrollLeft - el.clientWidth;
        if (distFromRight < SCROLL_TRIGGER_PX) {
            isFetchingMore.current = true;
            setCurrentCursor(pendingNextCursor);
        }
    }, [isFetching, hasMore, pendingNextCursor]);
    // Draw chart into SVG via useEffect so D3 work runs off the render thread
    // (must be before any early return to satisfy Rules of Hooks)
    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
        if (!allTrendsData.length)
            return;
        // SVG grows as more cursor pages load; base width = full 30-day window
        const TOTAL_DAYS = Math.max(1, Math.round((new Date(baseToDate + "T00:00:00Z").getTime() - new Date(baseFromDate + "T00:00:00Z").getTime()) /
            (1000 * 60 * 60 * 24)) + 1);
        const dynamicWidth = Math.max(width, TOTAL_DAYS * PIXELS_PER_DAY);
        const assetTrendsData = allTrendsData;
        const parseTimestamp = (timestampStr) => {
            if (!timestampStr)
                return new Date();
            if (appConfig?.release_1_1?.showCestTimezone) {
                try {
                    let datePart;
                    let timePart;
                    if (!timestampStr.includes("T")) {
                        datePart = baseToDate;
                        timePart = timestampStr;
                    }
                    else {
                        [datePart, timePart] = timestampStr.split("T");
                    }
                    const [year, month, day] = datePart.split("-").map(Number);
                    const [hours, minutes, seconds] = timePart.split(":").map((v) => Math.floor(Number(v)));
                    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
                }
                catch {
                    return new Date();
                }
            }
            return new Date(timestampStr);
        };
        const trendsData = appConfig?.release_1_1?.showCestTimezone
            ? assetTrendsData
            : assetTrendsData.map((data) => {
                let timestamp = data.timestamp || "";
                if (!timestamp.includes("T")) {
                    timestamp = `${baseToDate}T${timestamp}`;
                }
                timestamp = timestamp.replace(/Z$/, "");
                return { ...data, timestamp };
            });
        const powerData = trendsData
            .filter((d) => d.power !== undefined)
            .map((d) => ({
            parsedDate: parseTimestamp(d.timestamp || ""),
            timestamp: d.timestamp,
            value: d.power ?? 0
        }));
        const socData = trendsData
            .filter((d) => d.soc !== undefined)
            .map((d) => ({ parsedDate: parseTimestamp(d.timestamp || ""), timestamp: d.timestamp, value: d.soc ?? 0 }));
        // Fixed SVG dimensions — chart stays the same size regardless of how many pages are loaded
        svg.style("position", "relative")
            .attr("width", dynamicWidth + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom);
        const chartArea = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
        chartArea
            .append("clipPath")
            .attr("id", "clip")
            .append("rect")
            .attr("width", dynamicWidth)
            .attr("height", height);
        const clippedArea = chartArea.append("g").attr("clip-path", "url(#clip)");
        // Use the fixed window boundaries as the x-axis domain — NOT d3.extent(data).
        // d3.extent collapses when there is only one data point (domain = [T, T])
        // which causes D3 to place the point at the centre of the scale.
        // The fixed domain ensures data points always appear at their correct position.
        const xDomainStart = new Date(baseFromDate + "T00:00:00Z");
        const xDomainEnd = new Date(baseToDate + "T23:59:59Z");
        // Reversed range: left side (x=0) = newest, right side (x=dynamicWidth) = oldest.
        // Scrolling right reveals progressively older data.
        const xScale = d3.scaleTime().domain([xDomainStart, xDomainEnd]).range([dynamicWidth, 0]);
        const yScaleSOC = d3.scaleLinear().domain([0, SOC_MAX]).range([height, 0]);
        const maxPower = powerData.length > 0 ? d3.max(powerData, (d) => d.value) || DEFAULT_MAX_POWER : DEFAULT_MAX_POWER;
        const yScalePower = d3.scaleLinear().domain([0, maxPower]).range([height, 0]);
        const xAxis = d3
            .axisBottom(xScale)
            .tickFormat((d) => d3.utcFormat("%b %d")(d))
            .ticks(d3.utcDay.every(1));
        const yAxisLeft = d3.axisLeft(yScaleSOC).ticks(AXIS_TICK_COUNT);
        const yAxisRight = d3
            .axisRight(yScalePower)
            .ticks(AXIS_TICK_COUNT)
            .tickValues(d3.range(0, maxPower + 1, maxPower / POWER_TICK_COUNT));
        chartArea
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${height})`)
            .call(xAxis)
            .selectAll("text")
            .style("text-anchor", "middle")
            .attr("font-size", AXIS_FONT_SIZE)
            .attr("fill", AXIS_TEXT_COLOR);
        chartArea.select(".x-axis .domain").attr("stroke", "none");
        chartArea
            .append("g")
            .attr("class", "y-axis-left")
            .call(yAxisLeft)
            .attr("font-size", AXIS_FONT_SIZE)
            .selectAll("text")
            .attr("fill", AXIS_TEXT_COLOR);
        chartArea.select(".y-axis-left .domain").attr("stroke", "none");
        chartArea
            .append("g")
            .attr("class", "y-axis-right")
            .attr("transform", `translate(${dynamicWidth}, 0)`)
            .call(yAxisRight)
            .attr("font-size", AXIS_FONT_SIZE)
            .selectAll("text")
            .attr("fill", AXIS_TEXT_COLOR);
        chartArea.select(".y-axis-right .domain").attr("stroke", "none");
        chartArea
            .append("text")
            .attr("fill", LABEL_TEXT_COLOR)
            .attr("transform", `translate(${dynamicWidth / 2}, ${height + margin.bottom - X_LABEL_Y_OFFSET})`)
            .style("text-anchor", "middle")
            .style("font", LABEL_FONT_FAMILY)
            .style("font-size", LABEL_FONT_SIZE)
            .style("font-weight", LABEL_FONT_WEIGHT)
            .text("Timeline");
        chartArea
            .append("text")
            .attr("transform", "rotate(-90)")
            .attr("y", 0 - margin.left + Y_LABEL_LEFT_OFFSET)
            .attr("x", 0 - height / 2)
            .attr("dy", "1em")
            .style("text-anchor", "middle")
            .style("font", LABEL_FONT_FAMILY)
            .style("font-size", LABEL_FONT_SIZE)
            .style("font-weight", LABEL_FONT_WEIGHT)
            .attr("fill", LABEL_TEXT_COLOR)
            .text("SOC (%)");
        chartArea
            .append("text")
            .attr("transform", "rotate(-90)")
            .attr("y", dynamicWidth + margin.right - Y_LABEL_RIGHT_OFFSET)
            .attr("x", 0 - height / 2)
            .attr("dy", "1em")
            .style("text-anchor", "middle")
            .style("font-family", LABEL_FONT_FAMILY)
            .style("font-size", LABEL_FONT_SIZE)
            .style("font-weight", LABEL_FONT_WEIGHT)
            .attr("fill", LABEL_TEXT_COLOR)
            .text("Power (kW)");
        const lineSOC = d3
            .line()
            .x((d) => xScale(d.parsedDate))
            .y((d) => yScaleSOC(d.value))
            .curve(d3.curveLinear);
        const linePower = d3
            .line()
            .x((d) => xScale(d.parsedDate))
            .y((d) => yScalePower(d.value))
            .curve(d3.curveLinear);
        // Draw lines only — no per-point circles.
        // With 5-min data a 7-day window has ~2016 points; individual circles
        // would create thousands of SVG elements and cause severe render lag.
        // A single <path> element handles any number of points efficiently.
        if (socData.length > 1) {
            clippedArea
                .append("path")
                .datum(socData)
                .attr("fill", "none")
                .attr("stroke", COLOR_SOC)
                .attr("stroke-width", LINE_STROKE_WIDTH)
                .attr("d", lineSOC);
        }
        else if (socData.length === 1) {
            clippedArea
                .append("circle")
                .attr("cx", xScale(socData[0].parsedDate))
                .attr("cy", yScaleSOC(socData[0].value))
                .attr("r", DATA_POINT_RADIUS)
                .attr("fill", COLOR_SOC);
        }
        if (powerData.length > 1) {
            clippedArea
                .append("path")
                .datum(powerData)
                .attr("fill", "none")
                .attr("stroke", COLOR_POWER)
                .attr("stroke-width", LINE_STROKE_WIDTH)
                .attr("d", linePower);
        }
        else if (powerData.length === 1) {
            clippedArea
                .append("circle")
                .attr("cx", xScale(powerData[0].parsedDate))
                .attr("cy", yScalePower(powerData[0].value))
                .attr("r", DATA_POINT_RADIUS)
                .attr("fill", COLOR_POWER);
        }
        const legend = chartArea
            .append("g")
            .attr("class", "legend")
            .attr("transform", `translate(${width - LEGEND_X_OFFSET}, ${LEGEND_Y_OFFSET})`);
        const legendItems = legend
            .selectAll(".legend-item")
            .data(LEGEND_DATA)
            .enter()
            .append("g")
            .attr("class", "legend-item")
            .attr("transform", (d, i) => `translate(${i * LEGEND_ITEM_SPACING}, 0)`);
        legendItems
            .append("rect")
            .attr("width", LEGEND_RECT_WIDTH)
            .attr("height", LEGEND_RECT_HEIGHT)
            .attr("fill", (d) => d.color);
        legendItems
            .append("text")
            .attr("x", LEGEND_TEXT_X)
            .attr("y", 1)
            .attr("dy", "0.35em")
            .style("font-size", LEGEND_FONT_SIZE)
            .style("font-weight", "normal")
            .text((d) => d.label);
        chartArea
            .selectAll(".grid-line-y")
            .data(yScaleSOC.ticks(5))
            .enter()
            .append("line")
            .attr("class", "grid-line-y")
            .attr("x1", 0)
            .attr("x2", dynamicWidth)
            .attr("y1", (d) => yScaleSOC(d))
            .attr("y2", (d) => yScaleSOC(d))
            .attr("stroke", GRID_LINE_COLOR)
            .attr("stroke-width", GRID_STROKE_WIDTH)
            .attr("stroke-dasharray", GRID_DASH_ARRAY);
    }, [allTrendsData, appConfig, formattedTodayDate, width, height, margin, baseFromDate, baseToDate]);
    if (!allTrendsData.length && !isFetching)
        return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[40vh]", children: _jsx("span", { children: "No data available for the selected asset." }) }));
    return (_jsxs("div", { className: "w-full overflow-x-auto", "data-test": "asset-details-linechart-container", onScroll: handleScroll, ref: containerRef, children: [_jsx("svg", { "data-test": "asset-details-linechart-svg", ref: svgRef }), isFetching && (_jsx("div", { className: "flex justify-center items-center py-2 text-sm text-gray-500", "data-test": "asset-details-linechart-loading", children: "Loading\u2026" }))] }));
};
export default AssetDetailsLineChart;
